import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, CheckCircle, Download, ShieldCheck } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import './ApplyNow.css';
import { trackEvent, trackSessionEvent } from '../lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTAKE_EMAIL = 'pgsrh.intake@gmail.com';
// Served by the backend with a Content-Disposition: attachment header so the
// packet downloads/saves to the device on desktop AND mobile (iOS/Android),
// instead of opening in an in-browser PDF viewer.
const COUNTY_PDF_URL = `${API}/download/lehigh-county-funding-application`;

const COUNTY_OPTIONS = ['Lehigh County', 'Northampton County', 'Other'];

const PHONE_FIELDS = [
  'phone',
  'primaryDoctorPhone',
  'contactPhone',
  'caseManagerPhone',
  'officerPhone',
  'mentalHealthProviderPhone',
];

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const ZIP_RE = /^\d{5}(-\d{4})?$/;

const INITIAL_STATE = {
  // honeypot
  website: '',
  // Section 1
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  raceEthnicity: '',
  sex: '',
  pronouns: '',
  phone: '',
  email: '',
  streetAddress: '',
  city: '',
  state: 'Pennsylvania',
  zip: '',
  county: '',
  heardAboutUs: '',
  heardAboutUsOther: '',
  // Section 2
  paymentMethod: '',
  paymentExplanation: '',
  // Section 3
  documents: [],
  // Section 4
  primaryDoctor: '',
  primaryDoctorPhone: '',
  dischargeDate: '',
  // Section 5
  treatmentTimes: '',
  appliedBefore: '',
  // Section 6
  contactName: '',
  contactPhone: '',
  contactEmail: '',
  contactRelationship: '',
  // Section 7
  sobrietyDate: '',
  noSobrietyDate: false,
  outpatientProgram: '',
  primaryDrug: '',
  treatmentFacility: '',
  insuranceCarrier: '',
  // Section 8
  hasCaseManager: '',
  caseManagerName: '',
  caseManagerPhone: '',
  caseManagerEmail: '',
  // Section 9
  mat: '',
  matType: '',
  medicalMarijuanaCard: '',
  takingMedications: '',
  currentMedications: '',
  // Section 10
  probation: '',
  parole: '',
  officerName: '',
  officerPhone: '',
  officerEmail: '',
  officerCounty: '',
  // Section 11
  mentalHealthDiagnosed: '',
  mentalHealthDiagnosis: '',
  receivingMentalHealth: '',
  mentalHealthProvider: '',
  mentalHealthProviderPhone: '',
  physicalDisability: '',
  physicalDisabilityExplain: '',
  // Section 12
  ackTrue: false,
  ackNoGuarantee: false,
  ackContact: false,
  fullLegalName: '',
};

const FIELD_LABELS = {
  firstName: 'First Name',
  lastName: 'Last Name',
  dateOfBirth: 'Date of Birth',
  raceEthnicity: 'Race/Ethnicity',
  sex: 'Sex',
  pronouns: 'Pronouns / Identified As',
  phone: 'Phone Number',
  email: 'Email Address',
  streetAddress: 'Home Street Address',
  city: 'City',
  state: 'State',
  zip: 'ZIP Code',
  county: 'County of Residence',
  heardAboutUs: 'How did you hear about us?',
  heardAboutUsOther: 'Please tell us how you heard about us',
  paymentMethod: 'How will you be paying?',
  paymentExplanation: 'Explanation of payment source',
  documents: 'Identification / employment documents',
  primaryDoctor: 'Primary Care Doctor or Medical Provider',
  primaryDoctorPhone: 'Provider Phone Number',
  dischargeDate: 'Expected or Actual Discharge Date',
  treatmentTimes: 'Number of times in treatment',
  appliedBefore: 'Previous application answer',
  contactName: 'Contact Full Name',
  contactPhone: 'Contact Phone Number',
  contactEmail: 'Contact Email Address',
  contactRelationship: 'Relationship to the Applicant',
  sobrietyDate: 'Sobriety Date',
  outpatientProgram: 'Recovery Outpatient Program',
  primaryDrug: 'Primary Drug of Choice',
  treatmentFacility: 'Current or Most Recent Treatment Facility',
  insuranceCarrier: 'Name of Insurance Carrier',
  hasCaseManager: 'Case manager answer',
  caseManagerName: "Case Manager's Full Name",
  caseManagerPhone: "Case Manager's Phone Number",
  caseManagerEmail: "Case Manager's Email Address",
  mat: 'MAT answer',
  matType: 'Type of MAT',
  medicalMarijuanaCard: 'Medical Marijuana Card answer',
  takingMedications: 'Medications answer',
  currentMedications: 'Current Medications',
  probation: 'Probation answer',
  parole: 'Parole answer',
  officerName: "Officer's Full Name",
  officerPhone: "Officer's Phone Number",
  officerEmail: "Officer's Email Address",
  officerCounty: "Officer's County or Office Location",
  mentalHealthDiagnosed: 'Mental health diagnosis answer',
  mentalHealthDiagnosis: 'Mental Health Diagnosis or Diagnoses',
  receivingMentalHealth: 'Mental health services answer',
  mentalHealthProvider: 'Mental Health Provider',
  mentalHealthProviderPhone: 'Mental Health Provider Phone Number',
  physicalDisability: 'Physical disability answer',
  physicalDisabilityExplain: 'Physical disability explanation',
  ackTrue: 'Acknowledgment of accuracy',
  ackNoGuarantee: 'Acknowledgment that placement is not guaranteed',
  ackContact: 'Acknowledgment of contact consent',
  fullLegalName: "Applicant's Full Legal Name",
};

function formatPhone(raw) {
  const digits = (raw || '').replace(/\D/g, '').slice(0, 10);
  const len = digits.length;
  if (len === 0) return '';
  if (len < 4) return `(${digits}`;
  if (len < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

// Reusable download section — shown before the form and on the success screen.
const CountyFormDownload = () => (
  <div className="county-download">
    <div className="county-download-head">
      <Download size={26} className="county-download-icon" />
      <h3 className="county-download-title">Lehigh County SCA Recovery Housing Funding Application Packet</h3>
    </div>
    <a
      href={COUNTY_PDF_URL}
      download="Lehigh-County-SCA-Recovery-Housing-Funding-Application-Packet.pdf"
      className="btn btn-primary county-download-btn"
    >
      <Download size={18} /> Download Lehigh County Funding Application
    </a>
    <p className="county-download-text">
      Applicants seeking Lehigh County SCA funding must download and complete this application packet.
      Complete every required page, include all required supporting documents, and obtain all necessary
      signatures. Once completed, email the entire packet as one submission to{' '}
      <a href={`mailto:${INTAKE_EMAIL}`} className="county-download-link">{INTAKE_EMAIL}</a>.
    </p>
    <p className="county-download-note">
      Do not send individual pages in separate emails. Please send the completed packet as one attachment
      or one combined submission.
    </p>
  </div>
);

const ApplyNow = () => {
  const { toast } = useToast();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState(INITIAL_STATE);
  const applicationStarted = React.useRef(false);
  const fundingInterestTracked = React.useRef(false);
  const applicationSubmitted = React.useRef(false);

  // Additive-only: if arriving via a funding CTA (#apply-form), smoothly scroll
  // to the application form. Does NOT touch form fields or submission logic.
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hash === '#apply-form') {
      const t = setTimeout(() => {
        const el = document.getElementById('apply-form');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
      return () => clearTimeout(t);
    }
  }, []);


  const todayStr = new Date().toISOString().split('T')[0];
  const dateCompletedDisplay = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  useEffect(() => {
    if (isSubmitted) {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
      }
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
  }, [isSubmitted]);

  const clearError = (name) => {
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name !== 'website' && !applicationStarted.current) {
      applicationStarted.current = true;
      trackSessionEvent('application_start', 'ga4_application_started');
    }

    if (
      name === 'paymentMethod'
      && (value === 'SCA Funding' || value === 'County Funding')
      && !fundingInterestTracked.current
    ) {
      fundingInterestTracked.current = true;
      trackSessionEvent('funding_interest', 'ga4_funding_interest', { funding_type: 'sca' });
    }

    if (type === 'checkbox' && name === 'documents') {
      const newDocs = checked
        ? [...formData.documents, value]
        : formData.documents.filter((d) => d !== value);
      setFormData({ ...formData, documents: newDocs });
      clearError('documents');
      return;
    }

    if (type === 'checkbox') {
      // boolean checkboxes: ack*, noSobrietyDate
      const next = { ...formData, [name]: checked };
      if (name === 'noSobrietyDate' && checked) {
        next.sobrietyDate = '';
      }
      setFormData(next);
      clearError(name);
      if (name === 'noSobrietyDate') clearError('sobrietyDate');
      return;
    }

    const newValue = PHONE_FIELDS.includes(name) ? formatPhone(value) : value;
    setFormData({ ...formData, [name]: newValue });
    clearError(name);
  };

  const isYes = (k) => formData[k] === 'Yes';

  const validate = () => {
    const e = {};
    const req = (name) => {
      if (!String(formData[name] ?? '').trim()) {
        e[name] = `${FIELD_LABELS[name]} is required.`;
      }
    };

    // Section 1
    ['firstName', 'lastName', 'raceEthnicity', 'sex', 'pronouns', 'phone', 'email',
      'streetAddress', 'city', 'state', 'zip', 'county', 'heardAboutUs'].forEach(req);
    if (formData.heardAboutUs === 'Other' && !formData.heardAboutUsOther.trim()) {
      e.heardAboutUsOther = 'Please tell us how you heard about us.';
    }
    req('dateOfBirth');
    if (formData.dateOfBirth && formData.dateOfBirth > todayStr) {
      e.dateOfBirth = 'Date of Birth cannot be in the future.';
    }
    if (formData.email && !EMAIL_RE.test(formData.email.trim())) {
      e.email = 'Please enter a valid email address.';
    }
    if (formData.zip && !ZIP_RE.test(formData.zip.trim())) {
      e.zip = 'Enter a valid 5-digit ZIP (ZIP+4 optional).';
    }

    // Section 2
    req('paymentMethod');
    if (['Private Pay', 'Insurance', 'Other', 'County Funding'].includes(formData.paymentMethod)) {
      if (!formData.paymentExplanation.trim()) e.paymentExplanation = 'Please explain the source of payment.';
    }

    // Section 3
    if (formData.documents.length === 0) e.documents = 'Please select at least one option.';

    // Section 4
    ['primaryDoctor', 'primaryDoctorPhone', 'dischargeDate'].forEach(req);

    // Section 5
    ['treatmentTimes', 'appliedBefore'].forEach(req);

    // Section 6
    ['contactName', 'contactPhone', 'contactEmail', 'contactRelationship'].forEach(req);
    if (formData.contactEmail && !EMAIL_RE.test(formData.contactEmail.trim())) {
      e.contactEmail = 'Please enter a valid email address.';
    }

    // Section 7
    if (!formData.noSobrietyDate) req('sobrietyDate');
    ['outpatientProgram', 'primaryDrug', 'treatmentFacility', 'insuranceCarrier'].forEach(req);

    // Section 8
    req('hasCaseManager');
    if (isYes('hasCaseManager')) {
      ['caseManagerName', 'caseManagerPhone', 'caseManagerEmail'].forEach(req);
      if (formData.caseManagerEmail && !EMAIL_RE.test(formData.caseManagerEmail.trim())) {
        e.caseManagerEmail = 'Please enter a valid email address.';
      }
    }

    // Section 9
    req('mat');
    if (isYes('mat')) req('matType');
    req('medicalMarijuanaCard');
    req('takingMedications');
    if (isYes('takingMedications')) req('currentMedications');

    // Section 10
    req('probation');
    req('parole');
    if (isYes('probation') || isYes('parole')) {
      ['officerName', 'officerPhone', 'officerEmail', 'officerCounty'].forEach(req);
      if (formData.officerEmail && !EMAIL_RE.test(formData.officerEmail.trim())) {
        e.officerEmail = 'Please enter a valid email address.';
      }
    }

    // Section 11
    req('mentalHealthDiagnosed');
    if (isYes('mentalHealthDiagnosed')) req('mentalHealthDiagnosis');
    req('receivingMentalHealth');
    if (isYes('receivingMentalHealth')) {
      req('mentalHealthProvider');
      req('mentalHealthProviderPhone');
    }
    req('physicalDisability');
    if (isYes('physicalDisability')) req('physicalDisabilityExplain');

    // Section 12
    if (!formData.ackTrue) e.ackTrue = 'Please confirm this acknowledgment.';
    if (!formData.ackNoGuarantee) e.ackNoGuarantee = 'Please confirm this acknowledgment.';
    if (!formData.ackContact) e.ackContact = 'Please confirm this acknowledgment.';
    req('fullLegalName');

    return e;
  };

  const focusFirstError = (errorMap) => {
    const firstName = Object.keys(errorMap)[0];
    if (!firstName) return;
    const el = document.getElementById(firstName) || document.querySelector(`[name="${firstName}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => { try { el.focus({ preventScroll: true }); } catch (_) { /* noop */ } }, 350);
    }
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (isSubmitting) return;

    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      focusFirstError(newErrors);
      const count = Object.keys(newErrors).length;
      toast({
        title: `Please complete ${count} required ${count === 1 ? 'field' : 'fields'}`,
        description: 'The missing fields are highlighted in red. Scroll up to review.',
        variant: 'destructive',
        duration: 8000,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/submit-application`, formData, {
        timeout: 30000,
        headers: { 'Content-Type': 'application/json' },
      });

      if (response?.data?.success) {
        if (!applicationSubmitted.current) {
          applicationSubmitted.current = true;
          trackEvent('application_submit');
        }
        setIsSubmitted(true);
        toast({
          title: 'Intake Screening Submitted',
          description: 'Your screening has been sent to ProSolve Global Solutions Recovery Houses.',
        });
      } else {
        throw new Error('Unexpected response from server.');
      }
    } catch (error) {
      const isTimeout = error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '');
      const isNetwork = !error.response && !isTimeout;
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      const serverMsg = typeof detail === 'string' ? detail : detail?.message;

      // If the server returned field-level errors, surface them inline.
      if (status === 422 && detail?.errors && typeof detail.errors === 'object') {
        setErrors(detail.errors);
        focusFirstError(detail.errors);
      }

      let title = 'Submission Failed';
      let description;
      if (isTimeout) {
        title = 'Connection Timed Out';
        description = 'Your submission may not have gone through. Please try again, or call 610-443-0175.';
      } else if (isNetwork) {
        title = 'Network Connection Issue';
        description = 'We could not reach the server. Please check your internet connection and try again, or call 610-443-0175.';
      } else if (serverMsg) {
        description = `${serverMsg}`;
      } else {
        description = 'Something went wrong on our end. Please try again, or call 610-443-0175 and we will help you over the phone.';
      }
      toast({ title, description, variant: 'destructive', duration: 10000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---- Inner render helpers (plain functions, NOT components — avoids remount) ----
  const errFor = (name) => (errors[name] ? <p className="field-error">{errors[name]}</p> : null);

  const textField = ({ name, label, type = 'text', placeholder = '', required = true, helper = '' }) => (
    <div className="form-group">
      <label htmlFor={name} className="form-label">
        {label} {required && <span className="required">*</span>}
      </label>
      <input
        type={type}
        id={name}
        name={name}
        value={formData[name]}
        onChange={handleChange}
        max={type === 'date' && name === 'dateOfBirth' ? todayStr : undefined}
        className={`form-input ${errors[name] ? 'input-invalid' : ''}`}
        aria-invalid={!!errors[name]}
        placeholder={placeholder}
        inputMode={PHONE_FIELDS.includes(name) ? 'tel' : undefined}
      />
      {helper && <p className="field-helper">{helper}</p>}
      {errFor(name)}
    </div>
  );

  const selectField = ({ name, label, options, required = true }) => (
    <div className="form-group">
      <label htmlFor={name} className="form-label">
        {label} {required && <span className="required">*</span>}
      </label>
      <select
        id={name}
        name={name}
        value={formData[name]}
        onChange={handleChange}
        className={`form-select ${errors[name] ? 'input-invalid' : ''}`}
        aria-invalid={!!errors[name]}
      >
        <option value="">Choose an option</option>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
      {errFor(name)}
    </div>
  );

  const textArea = ({ name, label, placeholder = '', rows = 3, required = true, helper = '' }) => (
    <div className="form-group">
      <label htmlFor={name} className="form-label">
        {label} {required && <span className="required">*</span>}
      </label>
      <textarea
        id={name}
        name={name}
        rows={rows}
        value={formData[name]}
        onChange={handleChange}
        className={`form-textarea ${errors[name] ? 'input-invalid' : ''}`}
        aria-invalid={!!errors[name]}
        placeholder={placeholder}
      />
      {helper && <p className="field-helper">{helper}</p>}
      {errFor(name)}
    </div>
  );

  const yesNo = ({ name, label }) => (
    <div className="form-group">
      <label className="form-label">
        {label} <span className="required">*</span>
      </label>
      <div className={`radio-group radio-group-inline ${errors[name] ? 'radio-group-invalid' : ''}`}>
        {['Yes', 'No'].map((opt) => (
          <label key={opt} className="radio-label">
            <input
              type="radio"
              name={name}
              value={opt}
              checked={formData[name] === opt}
              onChange={handleChange}
            />
            <span>{opt}</span>
          </label>
        ))}
      </div>
      {errFor(name)}
    </div>
  );

  if (isSubmitted) {
    return (
      <div className="apply-page">
        <section className="page-hero">
          <div className="hero-container">
            <h1 className="page-title">Intake Screening Submitted</h1>
            <p className="page-subtitle">Thank you for your submission</p>
          </div>
        </section>

        <section className="success-section">
          <div className="content-container">
            <div className="success-card">
              <CheckCircle size={64} className="success-icon" />
              <h2 className="success-title">Your Intake Screening Has Been Received</h2>
              <p className="success-text">
                Thank you. Your intake screening has been submitted to ProSolve Global Solutions Recovery
                Houses. Completing this form does not guarantee acceptance, placement, county funding, or
                eligibility. Please follow the instructions below to complete any required county paperwork.
              </p>

              <CountyFormDownload />

              <div className="success-contact">
                <p className="contact-label">For immediate assistance, please call:</p>
                <a href="tel:610-443-0175" className="contact-phone">610-443-0175</a>
              </div>
              <button
                onClick={() => { setFormData(INITIAL_STATE); setErrors({}); setIsSubmitted(false); }}
                className="btn btn-secondary"
              >
                Submit Another Screening
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="apply-page">
      <section className="page-hero">
        <div className="hero-container">
          <h1 className="page-title">Recovery Housing Intake and Eligibility Screening</h1>
          <p className="page-subtitle">Begin Your Journey to Recovery</p>
        </div>
      </section>

      <section className="page-content" id="apply-form">
        <div className="content-container">
          <div className="form-intro">
            <FileText size={40} className="intro-icon" />
            <h2 className="intro-title">Intake Screening Form</h2>
            <p className="intro-text">
              Please complete all required questions. These questions are required by the applicable county
              and are used to determine whether an applicant&rsquo;s screening is eligible for further review.
              Submission of this form does not guarantee acceptance, placement, county funding, or approval.
            </p>
          </div>

          {/* County form download — placement #1 (before the form) */}
          <CountyFormDownload />

          <form onSubmit={handleSubmit} className="application-form" noValidate>
            {/* Honeypot — hidden from users, catches bots */}
            <div className="hp-field" aria-hidden="true">
              <label htmlFor="website">Website</label>
              <input
                type="text"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                tabIndex="-1"
                autoComplete="off"
              />
            </div>

            {Object.keys(errors).length > 0 && (
              <div className="form-error-summary" role="alert" data-testid="form-error-summary">
                <div className="form-error-summary-header">
                  <span className="form-error-summary-icon" aria-hidden="true">!</span>
                  <h3 className="form-error-summary-title">
                    Please complete the following required {Object.keys(errors).length === 1 ? 'field' : 'fields'}:
                  </h3>
                </div>
                <ul className="form-error-summary-list">
                  {Object.keys(errors).map((name) => (
                    <li key={name}>
                      <button
                        type="button"
                        className="form-error-summary-link"
                        onClick={() => {
                          const el = document.getElementById(name) || document.querySelector(`[name="${name}"]`);
                          if (el) {
                            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            setTimeout(() => { try { el.focus(); } catch (_) { /* noop */ } }, 350);
                          }
                        }}
                      >
                        {FIELD_LABELS[name] || name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* SECTION 1 */}
            <div className="form-section">
              <h3 className="section-title">Section 1 &mdash; Applicant Information</h3>
              <div className="form-row">
                {textField({ name: 'firstName', label: 'First Name', placeholder: 'First Name' })}
                {textField({ name: 'lastName', label: 'Last Name', placeholder: 'Last Name' })}
              </div>
              <div className="form-row">
                {textField({ name: 'dateOfBirth', label: 'Date of Birth', type: 'date' })}
                {selectField({
                  name: 'raceEthnicity',
                  label: 'Race/Ethnicity',
                  options: [
                    'American Indian or Alaska Native',
                    'Asian',
                    'Black or African American',
                    'Hispanic or Latino',
                    'Native Hawaiian or Pacific Islander',
                    'White',
                    'Two or More Races',
                    'Other',
                    'Prefer not to answer',
                  ],
                })}
              </div>
              <div className="form-row">
                {selectField({
                  name: 'sex',
                  label: 'Sex',
                  options: ['Female', 'Male', 'Intersex', 'Other', 'Prefer not to answer'],
                })}
                {textField({ name: 'pronouns', label: 'Pronouns / Identified As', placeholder: 'she/her, he/him, they/them' })}
              </div>
              <div className="form-row">
                {textField({ name: 'phone', label: 'Phone Number', type: 'tel', placeholder: '(555) 555-5555' })}
                {textField({ name: 'email', label: 'Email Address', type: 'email', placeholder: 'name@email.com' })}
              </div>
              <div className="form-row">
                {textField({ name: 'streetAddress', label: 'Home Street Address', placeholder: '123 Main St' })}
                {textField({ name: 'city', label: 'City', placeholder: 'City' })}
              </div>
              <div className="form-row">
                {textField({ name: 'state', label: 'State', placeholder: 'Pennsylvania' })}
                {textField({ name: 'zip', label: 'ZIP Code', placeholder: '12345 or 12345-6789' })}
              </div>
              <div className="form-row">
                {selectField({ name: 'county', label: 'County of Residence', options: COUNTY_OPTIONS })}
                <div className="form-group" />
              </div>

              {formData.county === 'Lehigh County' && (
                <div className="county-notice county-notice-info">
                  <ShieldCheck size={20} />
                  <span>
                    You selected Lehigh County. If you are seeking SCA funding, please download and complete
                    the Lehigh County Funding Application Packet shown above.
                  </span>
                </div>
              )}
              {formData.county && formData.county !== 'Lehigh County' && (
                <div className="county-notice">
                  <span>
                    County funding documentation may vary by county. A ProSolve representative may contact you
                    regarding additional required forms.
                  </span>
                </div>
              )}

              <div className="form-row">
                {selectField({
                  name: 'heardAboutUs',
                  label: 'How did you hear about us?',
                  options: ['Google', 'Facebook', 'Instagram', 'Other'],
                })}
                {formData.heardAboutUs === 'Other'
                  ? textField({ name: 'heardAboutUsOther', label: 'Please tell us how you heard about us', placeholder: 'Tell us how you found us' })
                  : <div className="form-group" />}
              </div>
            </div>

            {/* SECTION 2 */}
            <div className="form-section">
              <h3 className="section-title">Section 2 &mdash; Payment and Funding</h3>
              {selectField({
                name: 'paymentMethod',
                label: 'How will you be paying?',
                options: ['SCA Funding', 'County Funding', 'Private Pay', 'Insurance', 'Other'],
              })}
              {['Private Pay', 'Insurance', 'Other', 'County Funding'].includes(formData.paymentMethod) &&
                textArea({
                  name: 'paymentExplanation',
                  label: 'Explain the source of payment',
                  placeholder:
                    formData.paymentMethod === 'County Funding'
                      ? 'Identify the county or funding source'
                      : 'Describe your source of payment',
                  helper:
                    formData.paymentMethod === 'County Funding'
                      ? 'For county-funded applicants, please identify the county or funding source.'
                      : '',
                })}
            </div>

            {/* SECTION 3 */}
            <div className="form-section">
              <h3 className="section-title">Section 3 &mdash; Identification and Employment Documents</h3>
              <div className="form-group">
                <label className="form-label">
                  Which necessary identification or employment documents do you currently have? <span className="required">*</span>
                </label>
                <div className={`checkbox-group ${errors.documents ? 'radio-group-invalid' : ''}`}>
                  {[
                    'Social Security Card',
                    'Birth Certificate',
                    'State-Issued Identification Card',
                    "Driver's License",
                    'Passport',
                    'Other',
                    'None',
                  ].map((doc) => (
                    <label key={doc} className="checkbox-label">
                      <input
                        type="checkbox"
                        name="documents"
                        value={doc}
                        checked={formData.documents.includes(doc)}
                        onChange={handleChange}
                      />
                      <span>{doc}</span>
                    </label>
                  ))}
                </div>
                {errFor('documents')}
              </div>
            </div>

            {/* SECTION 4 */}
            <div className="form-section">
              <h3 className="section-title">Section 4 &mdash; Medical and Discharge Information</h3>
              <div className="form-row">
                {textField({ name: 'primaryDoctor', label: 'Name of Primary Care Doctor or Medical Provider' })}
                {textField({ name: 'primaryDoctorPhone', label: 'Provider Phone Number', type: 'tel', placeholder: '(555) 555-5555' })}
              </div>
              {textField({ name: 'dischargeDate', label: 'Expected or Actual Discharge Date', type: 'date' })}
            </div>

            {/* SECTION 5 */}
            <div className="form-section">
              <h3 className="section-title">Section 5 &mdash; Previous Treatment and Housing History</h3>
              {selectField({
                name: 'treatmentTimes',
                label: 'How many times have you been in treatment?',
                options: ['This is my first time', '1–3 times', '4–6 times', '7 or more times'],
              })}
              {yesNo({ name: 'appliedBefore', label: 'Have you applied for housing with ProSolve Global Solutions Recovery Houses before?' })}
            </div>

            {/* SECTION 6 */}
            <div className="form-section">
              <h3 className="section-title">Section 6 &mdash; Emergency or Support Contact</h3>
              <div className="form-row">
                {textField({ name: 'contactName', label: 'Contact Full Name' })}
                {textField({ name: 'contactPhone', label: 'Contact Phone Number', type: 'tel', placeholder: '(555) 555-5555' })}
              </div>
              <div className="form-row">
                {textField({ name: 'contactEmail', label: 'Contact Email Address', type: 'email', placeholder: 'name@email.com' })}
                {textField({ name: 'contactRelationship', label: 'Relationship to the Applicant', placeholder: 'e.g., Parent, Sibling, Friend' })}
              </div>
            </div>

            {/* SECTION 7 */}
            <div className="form-section">
              <h3 className="section-title">Section 7 &mdash; Recovery and Treatment Information</h3>
              {textField({
                name: 'sobrietyDate',
                label: 'Sobriety Date',
                type: 'date',
                required: !formData.noSobrietyDate,
              })}
              <label className="checkbox-label checkbox-inline">
                <input
                  type="checkbox"
                  name="noSobrietyDate"
                  checked={formData.noSobrietyDate}
                  onChange={handleChange}
                />
                <span>I do not currently have a sobriety date</span>
              </label>
              <div className="form-row">
                {textField({ name: 'outpatientProgram', label: 'Recovery Outpatient Program', placeholder: 'Program name (or "None")' })}
                {textField({ name: 'primaryDrug', label: 'Primary Drug of Choice' })}
              </div>
              <div className="form-row">
                {textField({ name: 'treatmentFacility', label: 'Current or Most Recent Treatment Facility' })}
                {textField({ name: 'insuranceCarrier', label: 'Name of Insurance Carrier', placeholder: 'Carrier name, "None", or "Uninsured"' })}
              </div>
            </div>

            {/* SECTION 8 */}
            <div className="form-section">
              <h3 className="section-title">Section 8 &mdash; Case Manager Information</h3>
              {yesNo({ name: 'hasCaseManager', label: 'Do you currently have a case manager?' })}
              {isYes('hasCaseManager') && (
                <>
                  {textField({ name: 'caseManagerName', label: "Case Manager's Full Name", placeholder: 'Full name (or "Not assigned")' })}
                  <div className="form-row">
                    {textField({ name: 'caseManagerPhone', label: "Case Manager's Phone Number", type: 'tel', placeholder: '(555) 555-5555' })}
                    {textField({ name: 'caseManagerEmail', label: "Case Manager's Email Address", type: 'email', placeholder: 'name@email.com' })}
                  </div>
                </>
              )}
            </div>

            {/* SECTION 9 */}
            <div className="form-section">
              <h3 className="section-title">Section 9 &mdash; Medication-Assisted Treatment and Medications</h3>
              {yesNo({ name: 'mat', label: 'Are you currently receiving Medication-Assisted Treatment (MAT)?' })}
              {isYes('mat') &&
                selectField({
                  name: 'matType',
                  label: 'Type of MAT',
                  options: ['Buprenorphine/Suboxone', 'Methadone', 'Naltrexone/Vivitrol', 'Other'],
                })}
              {yesNo({ name: 'medicalMarijuanaCard', label: 'Do you have a Medical Marijuana Card?' })}
              {yesNo({ name: 'takingMedications', label: 'Are you currently taking any prescribed or over-the-counter medications?' })}
              {isYes('takingMedications') &&
                textArea({
                  name: 'currentMedications',
                  label: 'Current Medications',
                  rows: 4,
                  placeholder: 'List each medication name, dosage (if known), and reason for taking it',
                  helper: 'Please list the medication name, dosage if known, and reason for taking it.',
                })}
            </div>

            {/* SECTION 10 */}
            <div className="form-section">
              <h3 className="section-title">Section 10 &mdash; Probation and Parole</h3>
              {yesNo({ name: 'probation', label: 'Are you currently on probation?' })}
              {yesNo({ name: 'parole', label: 'Are you currently on parole?' })}
              {(isYes('probation') || isYes('parole')) && (
                <>
                  <div className="form-row">
                    {textField({ name: 'officerName', label: "Officer's Full Name" })}
                    {textField({ name: 'officerPhone', label: "Officer's Phone Number", type: 'tel', placeholder: '(555) 555-5555' })}
                  </div>
                  <div className="form-row">
                    {textField({ name: 'officerEmail', label: "Officer's Email Address", type: 'email', placeholder: 'name@email.com' })}
                    {textField({ name: 'officerCounty', label: "Officer's County or Office Location" })}
                  </div>
                </>
              )}
            </div>

            {/* SECTION 11 */}
            <div className="form-section">
              <h3 className="section-title">Section 11 &mdash; Mental and Physical Health</h3>
              {yesNo({ name: 'mentalHealthDiagnosed', label: 'Have you been diagnosed with a mental health condition?' })}
              {isYes('mentalHealthDiagnosed') &&
                textArea({
                  name: 'mentalHealthDiagnosis',
                  label: 'Mental Health Diagnosis or Diagnoses',
                  rows: 3,
                  placeholder: 'List your diagnosis or diagnoses',
                })}
              {yesNo({ name: 'receivingMentalHealth', label: 'Are you currently receiving mental health services?' })}
              {isYes('receivingMentalHealth') && (
                <div className="form-row">
                  {textField({ name: 'mentalHealthProvider', label: 'Mental Health Provider', placeholder: 'Provider, agency, therapist, psychiatrist, or facility' })}
                  {textField({ name: 'mentalHealthProviderPhone', label: 'Mental Health Provider Phone Number', type: 'tel', placeholder: '(555) 555-5555' })}
                </div>
              )}
              {yesNo({ name: 'physicalDisability', label: 'Do you have any physical disabilities, mobility limitations, or accessibility needs?' })}
              {isYes('physicalDisability') &&
                textArea({
                  name: 'physicalDisabilityExplain',
                  label: 'Please explain any physical disability, mobility limitation, or accommodation needed',
                  rows: 3,
                })}
            </div>

            {/* SECTION 12 */}
            <div className="form-section">
              <h3 className="section-title">Section 12 &mdash; Applicant Acknowledgment</h3>
              <div className="form-group">
                <label className={`checkbox-label checkbox-ack ${errors.ackTrue ? 'ack-invalid' : ''}`}>
                  <input type="checkbox" name="ackTrue" checked={formData.ackTrue} onChange={handleChange} />
                  <span>I certify that the information I provided is true and complete to the best of my knowledge. <span className="required">*</span></span>
                </label>
                {errFor('ackTrue')}
              </div>
              <div className="form-group">
                <label className={`checkbox-label checkbox-ack ${errors.ackNoGuarantee ? 'ack-invalid' : ''}`}>
                  <input type="checkbox" name="ackNoGuarantee" checked={formData.ackNoGuarantee} onChange={handleChange} />
                  <span>I understand that completing this screening does not guarantee placement, acceptance, eligibility, county funding, or approval. <span className="required">*</span></span>
                </label>
                {errFor('ackNoGuarantee')}
              </div>
              <div className="form-group">
                <label className={`checkbox-label checkbox-ack ${errors.ackContact ? 'ack-invalid' : ''}`}>
                  <input type="checkbox" name="ackContact" checked={formData.ackContact} onChange={handleChange} />
                  <span>I understand that ProSolve Global Solutions Recovery Houses may contact me, my case manager, treatment provider, emergency contact, county agency, probation officer, or parole officer as necessary to review my application, subject to applicable consent and privacy requirements. <span className="required">*</span></span>
                </label>
                {errFor('ackContact')}
              </div>

              <div className="form-row" style={{ marginTop: '1rem' }}>
                {textField({ name: 'fullLegalName', label: "Applicant's Full Legal Name" })}
                <div className="form-group">
                  <label className="form-label">Date Completed</label>
                  <input type="text" className="form-input" value={dateCompletedDisplay} readOnly disabled />
                  <p className="field-helper">This is today&rsquo;s date and will be recorded as your submission date.</p>
                </div>
              </div>
            </div>

            <div className="privacy-notice">
              <ShieldCheck size={20} className="privacy-notice-icon" />
              <p>
                This form collects personal, medical, treatment, insurance, and criminal justice information
                for intake-screening purposes. Please verify that your information is accurate before submitting.
              </p>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary btn-submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <><span className="spinner" aria-hidden="true" /> Submitting&hellip;</>
                ) : (
                  'Submit Intake Screening'
                )}
              </button>
              <p className="privacy-note">
                Your information is confidential and will only be used for intake purposes.
              </p>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default ApplyNow;
